import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useSortBy } from 'react-instantsearch';

import type { EntityType } from '../config/SearchEngine/entityFields';

/**
 * Hook pour la configuration du tri des entités (auteurs, éditeurs, relieurs)
 *
 * @param entityType - Type d'entité (auteurs, editeurs, relieurs)
 * @param indexName - Nom de l'index Meilisearch
 * @param prefix - Préfixe optionnel pour les labels
 * @returns Configuration du tri et méthodes pour l'utiliser
 */
export function useEntitySortConfig(
  entityType: EntityType,
  indexName: string,
  prefix = '',
) {
  const t = useTranslations('EntitySearch.Sort');

  const config = useMemo(() => {
    // Configuration commune à tous les types d'entités
    const commonItems = [
      {
        value: `${indexName}`,
        label: `${prefix}${t('nb_livres_desc')}`,
      },
      {
        value: `${indexName}:nb_livres:asc`,
        label: `${prefix}${t('nb_livres_asc')}`,
      },
      {
        value: `${indexName}:nom:asc`,
        label: `${prefix}${t('nom_asc')}`,
      },
      {
        value: `${indexName}:nom:desc`,
        label: `${prefix}${t('nom_desc')}`,
      },
    ];

    // Configuration spécifique aux auteurs
    if (entityType === 'auteurs') {
      return {
        defaultValue: indexName,
        items: [
          ...commonItems,
          {
            value: `${indexName}:siecle_principal:asc`,
            label: `${prefix}${t('siecle_asc')}`,
          },
          {
            value: `${indexName}:siecle_principal:desc`,
            label: `${prefix}${t('siecle_desc')}`,
          },
        ],
      };
    }

    // Configuration par défaut pour éditeurs et relieurs (seulement tri par nom)
    return {
      defaultValue: `${indexName}:nb_livres:desc`,
      items: commonItems,
    };
  }, [entityType, indexName, prefix, t]);

  const { currentRefinement, refine, options } = useSortBy({
    items: config.items,
  });

  return { config, currentRefinement, refine, options };
}
